<template>
  <div class="md-layout stats-container">
    <!-- Tabs Navigation -->
    <div class="tabs-toggle-container md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
      <!-- Time Buttons -->
      <div class="tab-buttons" ref="tabsContainer">
        <md-button v-for="range in ['Day', 'Week', 'Month', 'Year']" :key="range" class="md-raised"
          :class="graphTimeRange === range ? 'md-success' : 'md-default'" @click="setGraphTimeRange(range)">
          {{ range }}
        </md-button>

        <!-- Custom button -->
        <md-button ref="customButton" class="md-raised"
          :class="graphTimeRange === 'Custom' ? 'md-success' : 'md-default'" @click="activateCustomRange">
          Custom
        </md-button>

        <!-- Range text -->
        <span style="padding-top: 13px;">
          Date Range: {{ formatDate(customRangeStart) }} - {{ formatDate(customRangeEnd) }}
        </span>

        <!-- Inline Calendar -->
        <div v-if="showCustomCalendar" class="custom-calendar-popup" :style="calendarPosition" ref="calendarPopup">
          <div class="calendar-header">
            <button class="month-nav" @click="prevMonth">&lt;</button>
            <span class="month-name">
              {{ currentMonth.toLocaleString('en-GB', { month: 'long', year: 'numeric' }) }}
            </span>
            <button class="month-nav" @click="nextMonth">&gt;</button>
          </div>
          <div class="calendar-grid">
            <div class="day-name" v-for="d in dayNames" :key="d">{{ d }}</div>
            <div class="calendar-day" v-for="day in daysInMonth" :key="day.date.toDateString()" :class="{
    selected: isSelected(day.date),
    inRange: isInRange(day.date),
    today: isToday(day.date)
    }" @click="selectDay(day.date)">
              {{ day.date.getDate() }}
            </div>
          </div>
        </div>
      </div>

      <div style="display: flex; align-items: center; gap: 24px; margin-left: auto">
        <!-- Set Electricity Price -->
        <div style="display: flex; flex-direction: column; gap: 4px;">

          <div class="toggle-switch" style="display: flex; align-items: center; gap: 9px;">
            <!-- Market price -->
            <span :class="{ 'selected-mode': !useFixedPrice }">Market</span>
            <span style="font-size:11px">({{ toTestFET(spotElectricityPrice) }} TESTFET/kWh)</span>
            <!-- Switch -->
            <label class="switch">
              <input type="checkbox" v-model="useFixedPrice" />
              <span class="slider"></span>
            </label>

            <!-- Fixed price -->
            <span :class="{ 'selected-mode': useFixedPrice }">Fixed</span>

            <!-- Input -->
            <input type="float"
              v-model="priceInputValue"
              :min="0"
              :max="10000"
              :step="0.00001"
              :readonly="!useFixedPrice"
              @blur="handlePriceBlur"
              @keydown.up.prevent="adjustPriceStep(1)"
              @keydown.down.prevent="adjustPriceStep(-1)"
              @keyup.enter="handlePriceBlur"
              class="custom-price-input"
              placeholder="0.00000"
              style="width: 100px;"
            />
            TESTFET/kWh
          </div>
        </div>

        <!-- Toggle: Energies / Spending -->
        <div class="toggle-switch">
          <span class="label-text">ENERGIES</span>
          <label class="switch">
            <input type="checkbox" v-model="showFinancial" @change="fetchTradingAnalytics" />
            <span class="slider"></span>
          </label>
          <span class="label-text">SPENDING</span>
        </div>
      </div>
    </div>

  
    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-50">
      <md-card header-animation="false" background-color="info" style="min-height: 400px">
        <md-card-header class="md-card-header-icon md-card-header-warning">
          <div class="card-icon">
            <md-icon>info_outline</md-icon>
          </div>
          <h4 class="title">
            Overview for selected period
            <Loader v-if="loadingGraphData" message="Loading data" />
          </h4>
        </md-card-header>

        <md-card-content class="card-content overview-card-content" v-if="!loadingGraphData">
          <div class="overview-section">

            <div class="overview-row">
              <span class="label">Consumer active power</span>
              <span class="value">
                <animated-number :value="currentSum" :duration="1000" /> 
                <span class="overview-unit">W</span>
              </span>
              
            </div>

            <div class="overview-row">
              <span class="label">Connected clients</span>
              <span class="value">
                <span class="overview-align">{{ connectedClients }}</span>
                </span>
            </div>

            <div class="overview-row">
              <span class="label">CO₂ Emissions</span>
              <span class="value">
                <span class="overview-align">{{ carbonFootprint }}</span>
                <span class="overview-unit">kg</span>
              </span>
            </div>

            <hr class="overview-divider" />

            <div class="overview-row total">
              <span class="label">TOTAL Capacity</span>
              <span class="value">
                <span class="overview-align">{{ producerCapacity.toFixed(2) }}</span>
                <span class="overview-unit">kWh</span>
              </span>
            </div>

            <div class="overview-row">
              <span class="label">Produced Energy</span>
              <span class="value">
                <span class="overview-align">{{ producedEnergy.toFixed(2) }}</span>
                <span class="overview-unit">kWh</span>
              </span>
            </div>

            <div class="overview-row">
              <span class="label">Stored Energy</span>
              <span class="value">
                <span class="overview-align">{{ Number(storedEnergy).toFixed(2) }}</span>
                <span class="overview-unit">kWh</span>
              </span>
            </div>

            <div class="overview-row">
              <span class="label">Sold Energy</span>
              <span class="value">
                <span class="overview-align">({{ this.soldEnergyFinancial.toFixed(2) }} €) {{ soldEnergy.toFixed(2) }}</span>
                <span class="overview-unit">kWh</span>
              </span>
            </div>

            <div class="overview-row">
              <span class="label">Wasted Energy</span>
              <span class="value">
                <span class="overview-align">{{ wastedEnergy.toFixed(2) < 0 ? 0 : wastedEnergy.toFixed(2) }}</span>
                <span class="overview-unit">kWh</span>
              </span>
            </div>

            <hr class="overview-divider" />

            <div class="overview-row">
              <span class="label">Energy Tokens</span>
              <span class="value">
                <span class="overview-align">{{ this.energyTokenCount }}</span>
              </span>
            </div>

            <div class="overview-row">
              <span class="label">EUA Count</span>
              <span class="value">
                <span class="overview-align">{{ this.euaAmount }}</span>
              </span>
            </div>
            
            <div class="overview-row">
              <span class="label">EUA Price:</span>
              <span class="value">
                <span class="overview-align">{{ this.euaCurrentPrice == 0.01 ? "Currently unavailable" : this.euaCurrentPrice + " €" }}</span>
              </span>
            </div>
            
            <div class="overview-row">
              <span class="label">Emission Factor</span>
              <span class="value">
                <span class="overview-align">{{ carbonEmissionFactor.toFixed(3) }}</span>
                <span class="overview-unit">kg CO₂/kWh</span>
              </span>
            </div>

          </div>
        </md-card-content>
      </md-card>
    </div>

    <!-- CARD 2: Smart Tips -->
    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-50">
      <md-card header-animation="false" background-color="info" style="height: 400px">
        <md-card-header class="md-card-header-icon md-card-header-warning">
          <div class="card-icon">
            <md-icon>lightbulb</md-icon>
          </div>
          <h4 class="title">
            Smart Tips
            <Loader v-if="loadingGraphData" message="Loading data" />
          </h4>

        </md-card-header>

        <md-card-content class="card-content smart-tips-scrollable" v-if="this.loadingGraphData === false">
          <SmartTips :totalCapacity="Number(producerCapacity.toFixed(2))"
            :producedEnergy="Number(producedEnergy.toFixed(2))" :storedEnergy="Number(storedEnergy.toFixed(2))"
            :soldPercentage="Number(soldEnergyPct.toFixed(2))" :wastedPercentage="Number(Math.max(wastedEnergyPct.toFixed(2), 0))" />
            <strong>AI Suggested Tips: </strong>
            <span class="smart-tips-text">{{ smartTipsText || 'Smart Tips provided by AI are currently loading...'}}</span>
        </md-card-content>
      </md-card>
    </div>  

  <!--
  <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-33">
      <stats-card header-color="blue">
        <template slot="header">
          <div class="card-icon">
            <md-icon>equalizer</md-icon>
          </div>
          <p class="category">Set electricity price (TESTFET/kWh)</p>

          <div class="price-input-wrapper">
            <input type="number" v-model="priceInputValue" :min="0" :max="10000" :step="0.00001"
              :readonly="!useFixedPrice" @blur="handlePriceBlur" @keydown.up.prevent="adjustPriceStep(1)"
              @keydown.down.prevent="adjustPriceStep(-1)" @keyup.enter="handlePriceBlur" class="custom-price-input"
              placeholder="0.00000" />
          </div>
        </template>

        <template slot="footer">
          <div class="stats" style="
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 10px;
            ">
            <div class="toggle-switch" style="display: flex; align-items: center; gap: 12px">
              <span>{{ convertFETToUSD(toTestFET(spotElectricityPrice)) }} €/kWh</span>
              <span :class="{ 'selected-mode': !useFixedPrice }">Market</span>
              <label class="switch">
                <input type="checkbox" v-model="useFixedPrice" />
                <span class="slider"></span>
              </label>
              <span :class="{ 'selected-mode': useFixedPrice }">Fixed</span>
              <div style="text-align: right">
                <span v-if="useFixedPrice">
                  {{ convertFETToUSD(toTestFET(energyToPriceRate)) }} €/kWh
                </span>
              </div>
            </div>

          </div>
        </template>
      </stats-card>
    </div>
  -->

    <!--
    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25">
      <stats-card header-color="rose">
        <template slot="header">
          <div class="card-icon">
            <md-icon>bolt</md-icon>
          </div>
          <p class="category">Market price (TESTFET/kWh)</p>
          <h3 class="title">
            {{ toTestFET(spotElectricityPrice) }}
          </h3>
        </template>
        <template slot="footer">
          <div class="stats">
            {{ convertFETToUSD(toTestFET(spotElectricityPrice)) }} €/kWh
          </div>
        </template>
      </stats-card>
    </div>
    -->

    <!--
    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-33">
      <stats-card header-color="green">
        <template slot="header">
          <div class="card-icon">
            <md-icon>lightbulb</md-icon>
          </div>
          <p class="category">Consumer active power</p>
          <h3 class="title">
            <animated-number :value="currentSum" :duration="1000" /> W
          </h3>
        </template>
        <template slot="footer">
          <div class="stats">
            Number of Connected Clients: {{ connectedClients }}
          </div>
        </template>
      </stats-card>
    </div>

    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-33">
      <stats-card header-color="warning">
        <template slot="header">
          <div class="card-icon">
            <md-icon>equalizer</md-icon>
          </div>
          <p class="category">Sold energy | CO₂ Emissions</p>
          <h3 class="title">
            {{ soldEnergy.toFixed(2) }} kWh | {{ carbonFootprint }} kg
          </h3>
        </template>
        <template slot="footer">
          <div class="stats" style="display: flex; align-items: center; gap: 6px">
            <img src="/img/green-dot.png" title="Online" style="width: 12px; height: 12px" />
            <span>Online</span>
          </div>
        </template>
      </stats-card>
    </div>
  -->

    <!-- Emission factor (kg CO₂ /kWh) -->
    <!--
    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25">
      <stats-card header-color="blue">
        <template slot="header">
          <div class="card-icon">
            <md-icon>co2</md-icon>
          </div>
          <p class="category">Emission factor (kg CO₂ /kWh)</p>
          <h3 class="title">
            {{ carbonEmissionFactor.toFixed(3) }} kg CO₂/kWh
          </h3>
        </template>
        <template slot="footer">
          <div class="stats">&nbsp;</div>
        </template>
      </stats-card>
    </div>
    -->
    <!-- Current EUA Price -->
    <!--
    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25">
      <stats-card header-color="green">
        <template slot="header">
          <div class="card-icon">
            <md-icon>euro</md-icon>
          </div>
          <p class="category">
            Current price - EUA
          </p>
          <h3 class="title">{{this.euaCurrentPrice == 0.01 ? "Currently unavailable" : this.euaCurrentPrice + " €"}}
          </h3>
        </template>
        <template slot="footer">
          <div class="stats">
            Trading Phase: 4 (2021 - 2030)
          </div>
        </template>

      </stats-card>
    </div>
    -->
    <!-- Number of European Union Allowances -->
    <!--
    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25">
      <stats-card header-color="rose">
        <template slot="header">
          <div class="card-icon">
            <md-icon>equalizer</md-icon>
          </div>
          <p class="category">Number of European Union Allowances</p>
          <h3 class="title">{{ this.euaAmount }}</h3>
        </template>
        <template slot="footer">
          <div class="stats">&nbsp;</div>
        </template>
      </stats-card>
    </div>
    -->
    <!--
    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25">
      <stats-card header-color="blue">
        <template slot="header">
          <div class="card-icon">
            <md-icon>note</md-icon>
          </div>
          <p class="category">Energy Tokens</p>
          <h3 class="title">
            {{ this.energyTokenCount }}
          </h3>
        </template>
        <template slot="footer">
          <div class="stats">&nbsp;</div>
        </template>
      </stats-card>
    </div>
    -->
    <!-- 🔋 End Static Stats Cards -->

    

    <!-- Info panel with percentages -->
    <!-- CARD 1: Generated Energy -->

    <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-50">
      <md-card header-animation="false" background-color="info" style="height: 400px">
        <md-card-header class="md-card-header-icon md-card-header-green">
          <div class="card-icon">
            <md-icon>bolt</md-icon>
          </div>
          <h4 class="title" v-if="this.loadingGraphData === true">
            Total capacity
            <Loader v-if="loadingGraphData" message="Loading data" />
          </h4>
          <h4 class="title" v-if="this.loadingGraphData === false">
            Total capacity - {{ producerCapacity.toFixed(2) }} kWh
          </h4>

        </md-card-header>

        <!--TODO
        <md-card-content class="card-content" v-if="this.loadingGraphData === false">
          <div class="section">
            <div class="section-labels two-columns">
              <span class="left">Battery capacity at the start: {{ initialStoredEnergy.toFixed(2) }} kWh</span>
            </div>

            <div class="progress-bar-container">
              <div class="bar stored-green" :style="{ width: batteryInitPct + '%' }"></div>
              <div class="bar wasted-gray" :style="{ width: batteryEmptyInitPct + '%' }"></div>
            </div>

            <div class="section-labels two-columns small">
              <span class="left">{{ batteryInitPct.toFixed(2) }}%</span>
            </div>
          </div>
        </md-card-content>
        -->

        <md-card-content class="card-content" style="margin-top:75px" v-if="this.loadingGraphData === false">
          <div class="section">
            <div class="section-labels two-columns">
              <span class="left">Produced: {{ producedEnergy.toFixed(2) }} kWh</span>
              <span class="right">Stored: {{ Number(storedEnergy).toFixed(2) }} kWh</span>
            </div>

            <div class="progress-bar-container">
              <div class="bar produced-yellow" :style="{ width: producedEnergyPct + '%' }"></div>
              <div class="bar stored-green" :style="{ width: storedEnergyPct + '%' }"></div>
            </div>

            <div class="section-labels two-columns small">
              <span class="left">{{ producedEnergyPct.toFixed(2) }}%</span>
              <span class="right">{{ storedEnergyPct.toFixed(2) }}%</span>
            </div>
          </div>
        </md-card-content>
        <md-card-content class="card-content" style="margin-top:75px" v-if="this.loadingGraphData === false">
          <div class="section">
            <div class="section-labels two-columns">
              <span class="left">Stored & Sold: {{ efficientlyUsedEnergy.toFixed(2) }} kWh</span>
              <span class="right">Wasted: {{ wastedEnergy.toFixed(2) < 0 ? 0 : wastedEnergy.toFixed(2) }} kWh</span>
            </div>

            <div class="progress-bar-container">
              <div class="bar sold-blue" :style="{ width: soldEnergyPct + '%' }"></div>
              <div class="bar wasted-gray" :style="{ width: wastedEnergyPct + '%' }"></div>
            </div>

            <div class="section-labels two-columns small">
              <span class="left">{{ soldEnergyPct.toFixed(2) > 100 ? 100 : soldEnergyPct.toFixed(2)}}%</span>
              <span class="right">{{ wastedEnergyPct.toFixed(2) < 0 ? 0 : wastedEnergyPct.toFixed(2) }}%</span>
            </div>
          </div>
        </md-card-content>
      </md-card>
    </div>

    <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-50">
      <md-card header-animation="false" background-color="danger" style="height: 400px">
        <md-card-header class="md-card-header-icon md-card-header-green">
          <div class="card-icon">
            <md-icon>insert_chart</md-icon>
          </div>
          <h4 class="title">Energy Produced/Stored/Sold/Predicted</h4>
        </md-card-header>
        <Loader v-if="loadingGraphData" message="Loading chart data" />
        <md-card-content style="height: 100%; padding: 1rem" v-if="this.loadingGraphData === false">
          <simple-line-chart :chart-data="chartDataYear" :chart-options="chartOptions" />
        </md-card-content>
      </md-card>
    </div>

    <!-- Emissions by Source -->
    <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-50">
      <md-card header-animation="false" style="height: 400px">
        <md-card-header class="md-card-header-icon md-card-header-rose">
          <div class="card-icon">
            <md-icon>bar_chart</md-icon>
          </div>
          <h4 class="title">Emissions produced</h4>
        </md-card-header>
        <md-card-content style="height: 100%; padding: 1rem">
          <simple-line-chart :chart-data="emissionSourceData" :chart-options="chartOptions" />
        </md-card-content>
      </md-card>
    </div>

    <!-- Chat Agent -->
    <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-50">
      <md-card header-animation="false" background-color="danger" class="chat-card" style="height: 400px">
        <!-- Card Header with Icon -->
        <md-card-header class="md-card-header-icon md-card-header-green">
          <div class="card-icon">
            <md-icon>chat_bubble_outline</md-icon>
            <!-- A chat icon -->
          </div>
          <h4 class="title">Chat with the agent</h4>
        </md-card-header>

        <!-- Card Content (Chat) -->
        <md-card-content class="chat-card-content">
          <div class="p-4 overflow-y-auto bg-white chat-messages-container" ref="chatContainer">
            <div v-for="(msg, index) in messages" :key="index" :class="[
                'flex items-start mb-4',
                msg.sender === 'user' ? 'justify-end' : 'justify-start',
              ]">
              <div :class="[
                  'max-w-xs p-3 rounded-lg text-white',
                  msg.sender === 'user'
                    ? 'bg-blue-600'
                    : 'bg-gray-300 text-gray-800',
                ]">
                {{ msg.text }}
              </div>
            </div>
          </div>
        </md-card-content>

        <!-- Card Actions (Message Input & Button) -->
        <md-card-actions>
          <form @submit.prevent="sendMessage" class="flex w-full gap-2 items-center">
            <!-- Replacing md-input with a standard HTML input -->
            <input v-model="newMessage" ref="messageInput" type="text" placeholder="Type your message..."
              class="input-field" :disabled="loading" required />
            <md-button type="submit" :class="[
                'md-raised md-primary',
                loading ? 'md-disabled' : '',
                'text-white disabled:cursor-not-allowed',
              ]" :disabled="loading">
              {{ loading ? "Waiting for response..." : "Send message" }}
            </md-button>
          </form>
        </md-card-actions>
      </md-card>
    </div>

    <!-- EUA Price Trend -->
    <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
      <md-card header-animation="false" style="height: 250px">
        <md-card-header class="md-card-header-icon md-card-header-info">
          <div class="card-icon">
            <md-icon>trending_up</md-icon>
          </div>
          <h4 class="title">EUA Price Trend</h4>
        </md-card-header>
        <md-card-content style="height: 100%; padding: 1rem">
          <simple-line-chart :chart-data="euaPriceData" :chart-options="chartOptions" />
        </md-card-content>
      </md-card>
    </div>

    <!-- Energy Storage -->
    <!--
    <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-50">
      <md-card header-animation="false" style="height: 400px">
        <md-card-header class="md-card-header-icon md-card-header-blue">
          <div class="card-icon">
            <md-icon>battery_full</md-icon>
          </div>
          <h4 class="title">Energy storage</h4>
        </md-card-header>
        <md-card-content style="height: 100%; padding: 1rem">
          <simple-line-chart
            :chart-data="energyStorageData"
            :chart-options="energyHistoryPredictionChartOptions"
          />
        </md-card-content>
      </md-card>
    </div>
    -->

    <!-- Energy ordering plan -->
    <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
      <md-card header-animation="false" background-color="danger" class="energyorderingplan-card"
        style="height: auto; z-index: 9999">
        <!-- Card Header with Icon -->
        <md-card-header class="md-card-header-icon md-card-header-rose"
          style="display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap">
          <!-- Názov a ikona -->
          <div style="display: flex; align-items: center; gap: 8px">
            <div class="card-icon">
              <md-icon>bolt</md-icon>
            </div>
            <h4 class="title" style="margin: 0">Energy ordering plan</h4>
          </div>

          <!-- Ovládacie prvky -->
          <div class="tab-buttons"
            style="display: flex; align-items: center; justify-content: space-between; width: 100%; gap: 12px; margin-top: 12px"
            ref="orderingTabsContainer">
            <!-- Ľavá strana -->
            <div style="flex: 1; display: flex; justify-content: flex-start; padding-left: 8px">
              <md-button class="md-dense" @click="adjustOrderingTime(-1)">&lt;&lt; -1h</md-button>
            </div>

            <!-- Stred -->
            <div style="flex: 1; display: flex; justify-content: center; gap: 8px">
              <md-button ref="orderingRangeButton" class="md-dense" @click="toggleOrderingCalendar">
                Select Day
              </md-button>
              <span style="padding-top: 4px"> Start From:</span>
              <input type="time" v-model="orderingSelectedTime"
                style="padding: 4px; border: 1px solid #ccc; border-radius: 4px" />

              <!-- Kalendár -->
              <div v-if="showOrderingCalendar" class="custom-calendar-popup" :style="orderingCalendarPosition"
                ref="orderingCalendarPopup">
                <div class="calendar-header">
                  <button class="month-nav" @click="prevOrderingMonth">&lt;</button>
                  <span class="month-name">
                    {{ orderingCurrentMonth.toLocaleString('en-GB', { month: 'long', year: 'numeric' }) }}
                  </span>
                  <button class="month-nav" @click="nextOrderingMonth">&gt;</button>
                </div>
                <div class="calendar-grid">
                  <div class="day-name" v-for="d in orderingDayNames" :key="d">{{ d }}</div>
                  <div class="calendar-day" v-for="day in orderingDaysInMonth" :key="day.date.toDateString()" :class="{
                    selected: isOrderingSelected(day.date),
                    today: isOrderingToday(day.date)
                  }" @click="selectOrderingDay(day.date)">
                    {{ day.date.getDate() }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Pravá strana -->
            <div style="flex: 1; display: flex; justify-content: flex-end; padding-right: 8px">
              <md-button class="md-dense" @click="adjustOrderingTime(1)">+1h &gt;&gt;</md-button>
            </div>
          </div>
        </md-card-header>



        <!-- Card Content (Energy ordering) -->
        <md-card-content class="energyorderingplan-card-content">
          <div class="energyorderingplan-container">
            <div v-if="energyOrderingDataOK">
              <div class="scroll-table">
                <table class="energy-table">
                  <thead>
                    <tr>
                      <th class="header-label">Source / Time</th>
                      <th v-for="i in columnCount" :key="'header-' + i" class="header-label">
                        {{
                        shouldShowHourLabel(i - 1)
                        ? formatOrderingPlanTime(i - 1)
                        : ""
                        }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(source, rowIndex) in sources" :key="'row-' + rowIndex">
                      <td class="source-name">{{ source }}</td>
                      <td v-for="(cell, colIndex) in matrix[rowIndex]" :key="'cell-' + rowIndex + '-' + colIndex"
                        :class="getCellClass(colIndex, cell)" :title="getEnergyTooltip(cell, rowIndex, colIndex)"></td>
                    </tr>
                  </tbody>
                  <!-- ✅ Move date row below the body -->
                  <tfoot>
                    <tr class="date-footer">
                      <td class="date-empty-cell"></td>
                      <!-- First empty cell -->
                      <td v-for="(group, index) in dateGroups" :key="'footer-' + index" class="date-cell"
                        :colspan="group.span">
                        {{ group.label }}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
            <!-- end of v-if=energyOrderingDataOK -->
            <div v-else>
              Agent doesn't support energy ordering plan at the moment.
            </div>
          </div>
          <!-- end of div energyorderingplan-container -->
        </md-card-content>
      </md-card>
    </div>
    <!-- end of energy ordering plan -->

    <div class="md-layout-item md-size-50">
      <!-- 🔹 Wallet Summary -->
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-info">
          <div class="card-icon">
            <md-icon>account_balance_wallet</md-icon>
          </div>
          <h4 class="title">Wallet Summary</h4>
        </md-card-header>

        <md-card-content>
          <md-table v-model="summaryRow" table-header-color="info">
            <md-table-row slot="md-table-row" slot-scope="{ item }">
              <md-table-cell md-label="Wallet Address">
                <a :href="
                    'https://companion.fetch.ai/dorado-1/accounts/' +
                    item.address
                  " target="_blank">
                  {{ item.address }}
                </a>
              </md-table-cell>
              <md-table-cell md-label="Current Balance (TESTFET)" class="md-align-right">{{ toTestFET(item.balance) }}
                TESTFET</md-table-cell>
              <md-table-cell md-label="EUR" class="md-align-right">
                <span v-if="item.usdAmount !== '-'">
                  {{ item.usdAmount }} €
                </span>
                <span v-else> Loading... </span>
              </md-table-cell>
            </md-table-row>
          </md-table>
        </md-card-content>
      </md-card>
    </div>
    <!-- 🔹 Agent information -->
    <div class="md-layout-item md-size-50">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-info">
          <div class="card-icon">
            <md-icon>smart_toy</md-icon>
          </div>
          <h4 class="title">Agentverse</h4>
        </md-card-header>
        <md-card-content>
          <md-table :value="summaryRow" table-header-color="info">
            <md-table-row slot="md-table-row" slot-scope="{ item }">
              <md-table-cell md-label="Agent ">
                <a :href="
                    'https://agentverse.ai/agents/details/' + item.agAddress
                  " target="_blank">
                  {{ item.agAddress }}
                </a>
              </md-table-cell>
            </md-table-row>
          </md-table>
        </md-card-content>
      </md-card>
    </div>

    <div class="md-layout-item md-size-100">
      <!-- 📜 Transaction History -->
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-green">
          <div class="card-icon">
            <md-icon>assignment</md-icon>
          </div>
          <h4 class="title">Transaction History (Last 20 in/out)</h4>
        </md-card-header>

        <md-card-content>
          <md-table v-model="paginatedTransactions" table-header-color="green">
            <md-table-row slot="md-table-row" slot-scope="{ item }">
              <md-table-cell md-label="Timestamp">{{
                formatTime(item.timestamp)
                }}</md-table-cell>
              <md-table-cell md-label="From"><span class="wallet-name" @click="copyToClipboard(item.from, $event)"
                  :title="item.from">
                  {{ item.fromName }}
                </span>
              </md-table-cell>
              <md-table-cell md-label="To"><span class="wallet-name" @click="copyToClipboard(item.to, $event)"
                  :title="item.to">
                  {{ item.toName }}
                </span>
              </md-table-cell>
              <md-table-cell md-label="Operation">{{
                item.operation
                }}</md-table-cell>
              <md-table-cell md-label="Amount (attoFET)">{{
                item.amount.toString()
                }}</md-table-cell>
              <md-table-cell md-label="Balance at Tx (attoFET)">{{
                item.balanceAfterTx.toString()
                }}</md-table-cell>
              <md-table-cell md-label="Tx Hash">
                <a :href="`https://companion.fetch.ai/dorado-1/transactions/${item.txhash}`" target="_blank"
                  rel="noopener noreferrer">
                  {{ item.txhash.slice(0, 12) }}...
                </a>
              </md-table-cell>
            </md-table-row>
          </md-table>

          <!-- Pagination Controls -->
          <div class="pagination-controls" style="
              display: flex;
              justify-content: space-between;
              margin-top: 1rem;
            ">
            <md-button @click="prevPage" :disabled="currentPage === 1">Previous</md-button>
            <span>Page {{ currentPage }} of {{ totalPages }}</span>
            <md-button @click="nextPage" :disabled="currentPage === totalPages">Next</md-button>
          </div>
        </md-card-content>
      </md-card>
    </div>
  </div>
</template>

<script>
import { StatsCard, AnimatedNumber } from "@/components";
import SimpleBarChart from "./SimpleBarChart.vue";
import { ref } from "vue";
import SimpleLineChart from "./SimpleLineChart.vue";
import emitter from "@/utils/eventBus";
import moment from "moment";
import Loader from "@/components/Loader.vue";
import SmartTips from '@/components/SmartTips.vue';
const chatContainer = ref(null);

import {
  baseUrl,
  customerByWallet,
  sellerByWallet,
  customerById,
  customers,
  sellers,
  sellerByPath,
  customerByPath,
  customerByAgent,
} from "../../utils/userInfo";
import { sortEventSegs } from "@fullcalendar/core/internal";

export default {
  data() {
    return {
      smartTipsText: "",
      maxEnergy: 0,
      initialStoredEnergy: 0,
      // Add the chat-related reactive data
      newMessage: "",
      messages: [],
      loading: false,
      chatContainer: null,
      sessionId: "session_" + Math.random().toString(36).substr(2, 9),

      priceInputValue: "",
      consumptionMatrix: [],
      fetPriceUSD: 0,
      balance: BigInt(0),
      address: "",
      agAddress: "",
      rawTransactions: [],
      transactionsWithBalance: [],
      currentPage: 1,
      itemsPerPage: 5,
      customer: customerById["gabriel"],
      seller: sellerByPath[this.$route.path],
      batterydirection: "backward",
      griddirection: "forward",
      power: 30,
      solarpower: 10,
      batterypower: 40,
      sources: [],
      matrix: [],
      energyOrderingDataOK: true, //default is true
      columnCount: 96,
      startTimestamp: 0,
      currentColumnIndex: null,
      // 🔋 Dynamic Stats
      chargingPower: 9.0,
      dischargingPower: 4.2,
      connectedClients: 0,
      connectedClientList: [],
      connectedClientLastValues: [],
      currentSecond: null,
      currentSum: 0,
      storedEnergy: 0,
      efficientlyUsedEnergy: 0,

      graphTimeRange: "Day",
      customRangeStart: null,
      customRangeEnd: null,
      showCustomPicker: false,
      showCustomCalendar: false,
      currentMonth: new Date(),
      selectedStart: null,
      selectedEnd: null,
      calendarPosition: { top: '0px', left: '0px' },
      dayNames: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],

      showOrderingCalendar: false,
      orderingCalendarPosition: { top: '0px', left: '0px' },
      orderingCurrentMonth: new Date(),
      orderingSelectedDate: null,
      orderingSelectedTime: '00:00',
      orderingDayNames: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
      overrideOrderingStartUnix: null,

      producerCapacity:0,
      soldEnergy:0,
      soldEnergyFinancial:0,
      wastedEnergy:0,
      producedEnergy:0,
      showFinancial: false, // prepínač medzi energiou a €
      loadingGraphData: true,
      energyToPriceRate: 0.0,
      alwaysUseCustomPrice: false,

      useFixedPrice: false,
      spotElectricityPrice: 0,

      carbonEmissionFactor: 0,
      soldenergythisyear: 0,

      euaCurrentPrice: 0,
      euaAmount: 0, //aktuálny počet povoleniek
      energyTokenCount: 0,

      chartDataYear: {
        labels: [],
        datasets: [
          {
            backgroundColor: "rgba(0, 200, 83, 0.7)",
            data: [],
          },
        ],
      },
      chartData14Days: {
        labels: [],
        datasets: [
          {
            backgroundColor: "rgba(0, 200, 83, 0.7)",
            data: [],
          },
        ],
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            ticks: {
              autoSkip: true,
            },
          },
          y: {
            beginAtZero: false,
          },
        },
        plugins: {
          legend: {
            display: true,
          },
        },
        elements: {
          bar: {
            minBarLength: 5,
          },
        },
      },
      energyHistoryPredictionChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            ticks: {
              autoSkip: false,
            },
          },
          y: {
            min: 0,
            max: 100,
            ticks: {
              stepSize: 20,
            },
            title: {
              display: true,
              text: 'Energy (kWh)'
            }
          },
        },
        plugins: {
          legend: {
            display: true,
          },
        },
        elements: {
          bar: {
            minBarLength: 5,
          },
        },
      },

      co2ChartData: {
          labels: [],
          datasets: [
            {
              label: "Number of EUAs",
              backgroundColor: "rgba(255, 152, 0, 0.5)",
              borderColor: "#ff9800",
              tension: 0.3,
              data: [],
            },
          ],
        },
        euaPriceData: {
          labels: [],
          datasets: [
            {
              label: "EUA Price (€/t)",
              borderColor: "#a569bd",
              backgroundColor: "rgba(165, 105, 189, 0.3)",
              data: [],
            },
          ],
        },
        emissionSourceData: {
          labels: [],
          datasets: [
            {
              label: "Emissions (kg CO₂)",
              backgroundColor: "rgba(244, 67, 54, 0.6)",
              data: [],
            },
          ],
        },
        energyStorageData: {
          labels: [],
          datasets: [
            {
              label: "Real energy stored (kWh)",
              borderColor: "rgba(11, 180, 200, 1)",
              backgroundColor: "rgba(11, 180, 200, 0.1)",
              pointBackgroundColor: "rgba(11, 180, 200, 1)",
              data: [],
              fill: false,
              tension: 0.3,
            },
            {
              label: "Predicted stored energy (kWh)",
              borderColor: "rgba(255, 193, 7, 1)",
              backgroundColor: "rgba(255, 193, 7, 0.1)",
              pointBackgroundColor: "rgba(255, 193, 7, 1)",
              borderDash: [5, 5],
              data: [],
              fill: false,
              tension: 0.3,
            },
          ],
        },
        energyHistoryData: {
          labels: [],
          datasets: [],
        },

        // Mock data pre historiu povoleniek, po implementacii vymazať
        allowanceHistory: [
          {
            timestamp: '2025-05-14T10:15:00Z',
            operation: 'Sold',
            quantity: 12,
            unit: 'EUA',
            value: '96 €'
          },
          {
            timestamp: '2025-05-13T15:20:00Z',
            operation: 'Purchased',
            quantity: 300,
            unit: 'kWh',
            value: '45 €'
          },
          {
            timestamp: '2025-05-12T08:45:00Z',
            operation: 'Received',
            quantity: 5,
            unit: 'EUA',
            value: 'Free Allocation'
          },
          {
            timestamp: '2025-05-11T17:05:00Z',
            operation: 'Sold',
            quantity: 200,
            unit: 'kWh',
            value: '28 €'
          },
          {
            timestamp: '2025-05-10T12:30:00Z',
            operation: 'Converted',
            quantity: 8,
            unit: 'EUA',
            value: '≈ 500 kWh'
          },
          {
            timestamp: '2025-05-09T11:00:00Z',
            operation: 'Purchased',
            quantity: 4,
            unit: 'EUA',
            value: '285 €'
          },
          {
            timestamp: '2025-05-08T09:00:00Z',
            operation: 'Sold',
            quantity: 150,
            unit: 'kWh',
            value: '19.5 €'
          }
        ],

    };
  },
  components: {
    //StatsCard,
    //SimpleBarChart,
    SimpleLineChart,
    AnimatedNumber,
    Loader,
    SmartTips,
  },

  created() {
    this.debouncedSendPriceUpdate = this.debounce(this.sendPriceUpdate, 500); // napr. 500ms delay
  },

  mounted() {
    document.addEventListener('click', this.handleClickOutside);
    document.addEventListener("click", this.handleOrderingClickOutside);
    this.updateEUACharts();
    this.fetchEUAPrice();
    this.chatContainer = this.$refs.chatContainer;
    this.onMounted();
    this.fetchFETPrice();
    this.updateSmartTips();

    this.fetchAgentDetails().then(() => {
      this.getBalance();
    });
    this.fetchTradingAnalytics();
    
    this.fetchData(); // ordering plan

    this.updateData().then(() => {
      this.fetchSpotPrice(); // spot price
    });
    /*this.fetchFETPrice().then(() => {
      this.fetchTradingAnalytics(); // až keď máme fetPriceUSD
    });*/

    const scheduleNextTransactionUpdate = () => {
      const now = new Date();
      const minutes = now.getMinutes();
      const nextUpdateIn =
        (15 - (minutes % 15)) * 60 * 1000 - now.getSeconds() * 1000;

      setTimeout(async () => {
        if (this.customer && this.$route.path === this.customer?.path) {
          await this.getBalance();
          await this.getTransactions();
          this.calculateHistoricalBalances();
        }
        scheduleNextTransactionUpdate();
      }, nextUpdateIn);
      emitter.off("update-values");
      emitter.on("update-values", ({ wsMessage, device }) => {
        if (!this.seller || this.$route.path !== this.seller.path) return;

        const phases = ["L1", "L2", "L3"];
        const sumPhases = (obj) => {
          let sum = 0;
          phases.forEach((phase) => {
            const value = obj?.[phase];
            if (typeof value === "number") {
              sum += value;
            }
          });
          return sum;
        };

        // kontroluj, či device patrí do aktívnych klientov
        if (this.connectedClientList.includes(device)) {
          const nowSecond = wsMessage.data.ts;

          // Ak je to nová sekunda, resetuj všetko
          if (this.currentSecond !== nowSecond) {
            this.currentSecond = nowSecond;
            this.connectedClientLastValues = {}; // resetuj mapu s hodnotami
          }

          // Zapíš najnovšiu hodnotu pre daný device
          const sum = sumPhases(wsMessage.data.P);
          this.connectedClientLastValues[device] = sum;

          // Ak už máme hodnoty od všetkých klientov, spočítaj súčet
          if (
            Object.keys(this.connectedClientLastValues).length ===
            this.connectedClientList.length
          ) {
            this.currentSum = Object.values(
              this.connectedClientLastValues
            ).reduce((acc, val) => acc + val, 0);
          }
        }
      });
    };

    this.priceInputValue = String(parseFloat(this.computedPrice).toFixed(5));

    setInterval(() => {
      this.checkTimeAndRefresh();
      this.fetchSpotPrice();
      this.fetchFETPrice();
    }, 60000); // každých 60 sekúnd

    scheduleNextTransactionUpdate();
  },
  watch: {
    computedPrice(newVal) {
      try {
        this.priceInputValue = String(parseFloat(newVal).toFixed(5));
      }
      catch(error) {}
      },
    useFixedPrice(newVal, oldVal) {
      if (newVal && !oldVal) {
        // Pri zapnutí fixed režimu nastavíme default na spot price
        this.energyToPriceRate = this.spotElectricityPrice;
        this.sendPriceUpdate(this.spotElectricityPrice);
      } else if (!newVal && oldVal) {
        // Pri vypnutí fixed režimu posielame 0
        this.sendPriceUpdate(0);
      }
    },
    "$route.path": function () {
      this.fetchData(); //fetch energy ordering plan
      this.fetchSpotPrice();
      this.graphTimeRange = "Day";
      this.showFinancial = false;
      this.fetchTradingAnalytics();
      this.updateData();
      this.getBalance();
      this.fetchAgentDetails();
      this.updateEUACharts();
      this.fetchEUAPrice();
      this.currentSum = 0;
      this.onMounted();
      this.smartTipsText = "Smart Tips provided by AI are currently loading...";
      this.updateSmartTips();
    },
    energyToPriceRate(newValue) {
      this.debouncedSendPriceUpdate(newValue);
    },
  },
  computed: {
    orderingDaysInMonth() {
      const days = [];
      const year = this.orderingCurrentMonth.getFullYear();
      const month = this.orderingCurrentMonth.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
      days.push({ date: new Date(d) });
      }
      return days;
    },

    daysInMonth() {
      const days = [];
      const year = this.currentMonth.getFullYear();
      const month = this.currentMonth.getMonth();
      const first = new Date(year, month, 1);
      const last = new Date(year, month + 1, 0);
      for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
        days.push({ date: new Date(d) });
      }
      return days;
      },
      

    producedEnergyPct() {
      return (this.producedEnergy / (this.producerCapacity - this.initialStoredEnergy)) * 100;
    },
    storedEnergyPct() {
      return (this.storedEnergy / (this.producerCapacity - this.initialStoredEnergy)) * 100;
    },
    soldEnergyPct() {
      return (this.efficientlyUsedEnergy / this.producerCapacity) * 100;
    },
    wastedEnergyPct() {
      return (this.wastedEnergy / this.producerCapacity) * 100;
    },
    carbonFootprint() {
      // Emisie CO2 (kg) = Spotreba elektriny (kWh) × Emisný faktor (kg CO2/kWh)
      return (this.soldEnergy * this.carbonEmissionFactor).toFixed(2);
    },
    batteryInitPct() {
      return (this.initialStoredEnergy / this.maxEnergyCapacity) * 100;
    },
    batteryEmptyInitPct() {
      return ((this.maxEnergyCapacity - this.initialStoredEnergy) / this.maxEnergyCapacity) * 100;
    },

    computedPrice: {
      get() {
        return this.useFixedPrice
          ? this.toTestFET(this.energyToPriceRate)
          : this.toTestFET(this.spotElectricityPrice);
      },
      set(value) {
        if (this.useFixedPrice) {
          this.energyToPriceRate = this.toAttoFET(parseFloat(value) || 0);
        }
      },
    },

    dateGroups() {
      if (!this.startTimestamp || this.columnCount === 0) {
        return []; // skip rendering until data is ready
      }

      const dates = [];
      let currentDate = "";
      let groupStart = 0;

      for (let i = 0; i < this.columnCount; i++) {
        const timestamp = this.startTimestamp + i * 15 * 60;
        const date = new Date(timestamp * 1000);
        const dateStr = date.toLocaleDateString("sk-SK"); // returns DD.MM.YYYY

        if (dateStr !== currentDate) {
          if (currentDate !== "") {
            dates.push({
              start: groupStart,
              span: i - groupStart,
              label: currentDate,
            });
          }
          currentDate = dateStr;
          groupStart = i;
        }
      }

      // Final group
      dates.push({
        start: groupStart,
        span: this.columnCount - groupStart,
        label: currentDate,
      });

      return dates;
    },

    summaryRow() {
      const fetAmount = Number(this.balance) / 1e18;
      const price = this.fetPriceUSD;
      const usdAmount = price ? (fetAmount * price).toFixed(2) : "-";

      return [
        {
          address: this.address,
          agAddress: this.agAddress,
          balance: this.balance?.toString?.() || "0",
          fetAmount,
          usdAmount,
        },
      ];
    },
    paginatedTransactions() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.transactionsWithBalance.slice(start, end);
    },
    totalPages() {
      return (
        Math.ceil(this.transactionsWithBalance.length / this.itemsPerPage) || 1
      );
    },
  },
  beforeDestroy() {
      document.removeEventListener('click', this.handleClickOutside);
      document.removeEventListener("click", this.handleOrderingClickOutside);
  },

  methods: {
      onMounted() {
        this.sessionId = "session_" + Math.random().toString(36).substr(2, 9);
        this.messages = [];
        if (chatContainer.value) {
          chatContainer.value.scrollTop = 0;
        }
        this.messages.push({
          sender: "bot",
          text:
            "I'm energy agent at " +
            this.seller.name +
            ". How can I assist you? ",
          session_id: this.sessionId,
        });
        if (chatContainer.value) {
          // Now it's safe to access the DOM element
          chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
        }
      },
      async sendMessage() {
        const userMsg = this.newMessage.trim();
        if (!userMsg) return;

        // Add user's message with session_id
        this.messages.push({
          sender: "user",
          text: userMsg,
          session_id: this.sessionId,
        });
        this.newMessage = "";
        this.loading = true;

        await this.$nextTick();

        if (this.chatContainer) {
          this.chatContainer.scrollTop = this.chatContainer.scrollHeight;
        } 
        try {
          let apiUrl = `${this.seller.agentUrl}/rest/post`;
          if (!apiUrl) apiUrl = `${baseUrl}/agent8/rest/post`;

          const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              text: userMsg,
              session_id: this.sessionId, // Add session_id to the request payload
            }),
          });

          const data = await response.json();

          // Add bot response with session_id
          this.messages.push({
            sender: "bot",
            text: data.text || "[No response text]",
            session_id: this.sessionId,
          });
          this.fetchData();
        } catch (error) {
          this.messages.push({
            sender: "bot",
            text: "[Error: Energy Agent is not responding...]",
            session_id: this.sessionId,
          });
        } finally {
          this.loading = false;

          await this.$nextTick();
          if (this.chatContainer) {
            this.chatContainer.scrollTop = this.chatContainer.scrollHeight;
          }
          this.$nextTick(() => {
            this.$refs.messageInput?.focus();
          });
        }
      },

      handleOrderingClickOutside(event) {
        if (
        this.showOrderingCalendar &&
        !this.$refs.orderingCalendarPopup?.contains(event.target) &&
        !this.$refs.orderingTabsContainer?.contains(event.target)
        ) {
          this.showOrderingCalendar = false;
        }
      },

      toggleOrderingCalendar() {
        this.showOrderingCalendar = !this.showOrderingCalendar;

        this.$nextTick(() => {
          const button = this.$refs.orderingRangeButton?.$el || this.$refs.orderingRangeButton;
          const container = this.$refs.orderingTabsContainer;

          if (button && container) {
            const btnRect = button.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();

            this.orderingCalendarPosition = {
              top: `${btnRect.bottom - containerRect.top}px`,
              left: `${btnRect.left - containerRect.left}px`,
            };
          }
        });
    },

      

      prevOrderingMonth() {
        this.orderingCurrentMonth = new Date(
        this.orderingCurrentMonth.getFullYear(),
        this.orderingCurrentMonth.getMonth() - 1,
        1
        );
      },
      nextOrderingMonth() {
        this.orderingCurrentMonth = new Date(
        this.orderingCurrentMonth.getFullYear(),
        this.orderingCurrentMonth.getMonth() + 1,
        1
        );
      },
      isOrderingSelected(date) {
        if (!this.orderingSelectedDate) return false;

        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        const dateStr = `${yyyy}-${mm}-${dd}`;

        return this.orderingSelectedDate === dateStr;
      },
      isOrderingToday(date) {
        const today = new Date();
        return date.toDateString() === today.toDateString();
        },
      selectOrderingDay(date) {
        this.orderingSelectedDate = new Date(date);
        this.showOrderingCalendar = false;

        // Nastav timestamp použitý vo fetchData
        const selectedTimeParts = this.orderingSelectedTime.split(":");
        const hours = parseInt(selectedTimeParts[0]);
        const minutes = parseInt(selectedTimeParts[1]);
        this.orderingSelectedTime = `${String(hours).padStart(2, '0')}:00`;

        this.orderingSelectedDate.setHours(hours);
        this.orderingSelectedDate.setMinutes(0);
        this.orderingSelectedDate.setSeconds(0);
        this.orderingSelectedDate.setMilliseconds(0);

        const unix = Math.floor(this.orderingSelectedDate.getTime() / 1000);
        this.overrideOrderingStartUnix = unix;

        this.fetchData();
      },



      adjustOrderingTime(hours) {
        if (!this.orderingSelectedDate || !this.orderingSelectedTime) return;

        const [h, m] = this.orderingSelectedTime.split(":").map(Number);
        const date = new Date(this.orderingSelectedDate);

        // Nastav aktuálny čas (s minútami na 0)
        date.setHours(h);
        date.setMinutes(0);
        date.setSeconds(0);
        date.setMilliseconds(0);

        // Pridaj alebo odober hodiny
        date.setHours(date.getHours() + hours);

        // Aktualizuj orderingSelectedDate (YYYY-MM-DD formát)
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        this.orderingSelectedDate = `${yyyy}-${mm}-${dd}`;

        // Aktualizuj orderingSelectedTime (HH:00 formát)
        const updatedHours = String(date.getHours()).padStart(2, '0');
        this.orderingSelectedTime = `${updatedHours}:00`;

        // Prepočet unix timestampu
        this.overrideOrderingStartUnix = Math.floor(date.getTime() / 1000);

        // Fetchni nové dáta
        this.refreshOrderingData();
      },

      refreshOrderingData() {
        if (!this.orderingSelectedDate || !this.orderingSelectedTime) return;
        const [h, m] = this.orderingSelectedTime.split(":").map(Number);
        const date = new Date(this.orderingSelectedDate);
        date.setHours(h);
        date.setMinutes(0);
        date.setSeconds(0);
        date.setMilliseconds(0);
        //const startUnixTimestamp = Math.floor(date.getTime() / 1000);
        this.overrideOrderingStartUnix = Math.floor(date.getTime() / 1000);
        this.fetchData();
      },


        async fetchDataWithStart(startUnixTimestamp) {
        // Tento method override-uje pôvodný fetchData()
        const hours = 12;
        const url = `${this.seller.agentUrl}/rest/energyorderingtable`;
        const requestData = {
        startUnixTimestamp,
        hours
        };


        try {
        const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData)
        });
        const data = await response.json();
        // ... existujúca logika načítania dát
        } catch (error) {
        this.energyOrderingDataOK = false;
        }
        },

      activateCustomRange(event) {
        this.graphTimeRange = 'Custom';
        this.selectedStart = null;
        this.selectedEnd = null;
        this.showCustomCalendar = true;

        this.$nextTick(() => {
          const buttonEl = this.$refs.customButton?.$el || this.$refs.customButton;
          const container = this.$refs.tabsContainer;

          if (buttonEl && container) {
            const btnRect = buttonEl.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();

            // Pozícia voči .tab-buttons
            const top = btnRect.bottom - containerRect.top + 8;
            const left = btnRect.left - containerRect.left;

            this.calendarPosition.top = `${top}px`;
            this.calendarPosition.left = `${left}px`;
          }
        });
      },

      selectDay(date) {
        if (!this.selectedStart || (this.selectedStart && this.selectedEnd)) {
          this.selectedStart = date;
          this.selectedEnd = null;
        } else if (date >= this.selectedStart) {
          this.selectedEnd = date;
          this.finalizeRange();
        } else {
          this.selectedStart = date;
          this.selectedEnd = null;
        }
      },


      finalizeRange() {
        this.customRangeStart = new Date(this.selectedStart.setHours(0, 0, 0, 0));
        this.customRangeEnd = new Date(this.selectedEnd.setHours(23, 59, 59, 999));
        this.showCustomCalendar = false;
        this.fetchTradingAnalytics();
      },

      isSelected(date) {
        return (
        (this.selectedStart && date.toDateString() === this.selectedStart.toDateString()) ||
        (this.selectedEnd && date.toDateString() === this.selectedEnd.toDateString())
        );
        },
        isInRange(date) {
        return (
        this.selectedStart &&
        this.selectedEnd &&
        date > this.selectedStart &&
        date < this.selectedEnd
        );
        },
        isToday(date) {
        const today = new Date();
        return date.toDateString() === today.toDateString();
      },


        prevMonth() {
          const prev = new Date(this.currentMonth);
          prev.setMonth(prev.getMonth() - 1);
          this.currentMonth = prev;
        },
        nextMonth() {
          const next = new Date(this.currentMonth);
          next.setMonth(next.getMonth() + 1);
          this.currentMonth = next;
        },


      handleClickOutside(e) {
        if (
          this.showCustomCalendar &&
          !this.$refs.calendarPopup.contains(e.target) &&
          !this.$refs.tabsContainer.contains(e.target)
        ) {
          this.showCustomCalendar = false;
        }
      },

    setGraphTimeRange(range) {
      this.graphTimeRange = range;

      this.showCustomCalendar = false;
      this.fetchTradingAnalytics();
    },

    toggleCustomRange() {
      this.graphTimeRange = "Custom";
      this.showCustomPicker = !this.showCustomPicker;

      // ak sa zobrazuje, vymaž hodnoty
      if (this.showCustomPicker) {
        this.customRangeStart = null;
        this.customRangeEnd = null;
      }
    },
    onCustomRangeChange() {
      if (this.customRangeStart && this.customRangeEnd && this.graphTimeRange === "Custom") {
        this.fetchTradingAnalytics();
      }
    },

    formatDate(date) {
      return date ? moment(date).format("DD.MM.YYYY") : "-";
    },

    async updateEUACharts() {

      try {
        const url = `${this.seller.agentUrl}/rest/permissionsHistory`;
        const endTimestamp = Math.floor(Date.now() / 1000);
        const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            startTimestamp: endTimestamp - 24 * 3600 * 7, //posledných 7dni
            endTimestamp,
            granularity: "",
            type: "",
          }),
        });

        const json = await response.json();
        const data = json.result || [];

        // Spracovanie dát do grafu za posledný týždeň
        const co2ChartLabels = data.map((entry) => {
          const date = new Date(entry.time * 1000);
          return date.toLocaleTimeString("en-GB", {
            month: "numeric",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          }); 
        });

        const co2ChartData = data.map((entry) => entry.permissionsBought);

        this.co2ChartData = {
        labels: co2ChartLabels,
        datasets: [
          {
            label: "Number of EUAs",
            backgroundColor: "rgba(255, 152, 0, 0.5)",
            borderColor: "#ff9800",
            tension: 0.3,
            data: co2ChartData,
          },
        ],
      };

      } catch (error) {
      }

      // EUA PRICE DATA
      try {
        const url = `${baseUrl}/agent15/rest/co2History`;
        const endTimestamp = Math.floor(Date.now() / 1000);
        const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            startTimestamp: endTimestamp - 14* 24 * 3600,
            endTimestamp: endTimestamp,
            granularity: "day",
            type: "",
          }),
        });

        const json = await response.json();
        const data = json.result || [];

        const euaPriceDataLabels = data.map((entry) => {
          const date = new Date(entry.time * 1000);
          return date.toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short"
          });
        });

        const euaData = data.map((entry) => entry.price);

        this.euaPriceData = {
          labels: euaPriceDataLabels,
          datasets: [
            {
              label: "EUA Price (€/t)",
              borderColor: "#a569bd",
              backgroundColor: "rgba(165, 105, 189, 0.3)",
              data: euaData,
            },
          ],
      };
        
      } catch (error) {
      }

      
      //EMISSIONS DATA + ENERGY STORAGE DATA + ENERGY PREDICTION
      /*try {
        const now = Math.floor(Date.now() / 1000);
        const oneDay = 24 * 3600;
        const historyUrl = `${this.seller.agentUrl}/rest/energyHistory`;
        const predictionUrl = `${this.seller.agentUrl}/rest/energyStatePrediction`;

        // === 1. FETCH HISTORICAL DATA ===
        const historyResponse = await fetch(historyUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            startTimestamp: now - oneDay,
            endTimestamp: now,
            granularity: "block",
            type: "",
          }),
        });

        const historyJson = await historyResponse.json();
        const historyData = historyJson.result || [];

        // === 2. FETCH PREDICTED DATA ===
        const predictionResponse = await fetch(predictionUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            stopUnixTimestamp: now + 2 * oneDay,
          }),
        });

        const predictionJson = await predictionResponse.json();
        const predictionList = predictionJson.predictionList || [];

        // === 3. LABELY
        // - pre emissions: len history
        // - pre energyStorage: history + prediction
        const historyTimestamps = historyData.map((entry) => new Date(entry.time * 1000));
        const predictionTimestamps = predictionList.map((entry) => new Date(entry.timestamp * 1000));
        const fullTimestamps = [...historyTimestamps, ...predictionTimestamps];

        const historyLabels = historyTimestamps.map((date) =>
          date.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
          })
        );

        const fullLabels = fullTimestamps.map((date) =>
          date.toLocaleTimeString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })
        );

        // === 4. EMISSION GRAPH (graph 1)
        const emissionData = historyData.map(
          (entry) => entry.soldEnergy * this.carbonEmissionFactor
        );

        this.emissionSourceData = {
          labels: historyLabels,
          datasets: [
            {
              label: "Emissions (kg CO₂)",
              backgroundColor: "rgba(244, 67, 54, 0.6)",
              borderColor: "rgba(244, 67, 54, 1)",
              data: emissionData,
              fill: false,
              tension: 0.3,
            },
          ],
        };

        // === 5. ENERGY STORAGE + PREDICTION
        /*
        const realEnergy = historyData.map((entry) => entry.energy);
        const predictedEnergy = predictionList.map((entry) => entry.availableEnergy);

        const energyStoredReal = [...realEnergy, ...new Array(predictedEnergy.length).fill(null)];
        const energyStoredPredicted = [
          ...new Array(realEnergy.length).fill(null),
          ...predictedEnergy,
        ];

        this.energyStorageData = {
          labels: fullLabels,
          datasets: [
            {
              label: "Real energy stored (kWh)",
              borderColor: "rgb(39, 174, 96)",
              backgroundColor: "rgb(85, 243, 151)",
              pointBackgroundColor: "rgba(11, 180, 200, 1)",
              data: energyStoredReal,
              fill: false,
              tension: 0.3,
            },
            {
              label: "Predicted energy (kWh)",
              borderColor: "rgba(120, 190, 120, 1)",
              backgroundColor: "rgba(144, 238, 144, 0.1)",
              pointBackgroundColor: "rgba(255, 193, 7, 1)",
              borderDash: [5, 5],
              data: energyStoredPredicted,
              fill: false,
              tension: 0.3,
            },
          ],
        };
        */

        // === 6. COMBINED HISTORY GRAPH
        /*
        const energyValues = historyData.map((entry) => entry.energy);
        const soldEnergyValues = historyData.map((entry) => entry.soldEnergy);
        const generatedPowerValues = historyData.map((entry) => entry.generatedPower);

        this.energyHistoryData = {
          labels: historyLabels,
          datasets: [
            {
              label: "Produced energy (kWh)",
              borderColor: "rgb(244, 208, 63)",
              backgroundColor: "rgb(247, 217, 99)",
              data: generatedPowerValues,
              fill: false,
              tension: 0.3,
            },
            {
              label: "Stored energy (kWh)",
              borderColor: "rgb(39, 174, 96)",
              backgroundColor: "rgb(85, 243, 151)",
              data: energyValues,
              fill: false,
              tension: 0.3,
            },
            {
              label: "Sold energy (kWh)",
              borderColor: "rgba(255, 87, 34, 1)",
              backgroundColor: "rgba(255, 87, 34, 0.2)",
              data: soldEnergyValues,
              fill: false,
              tension: 0.3,
            },
          ],
        };
        
      } catch (error) {}
      */
    },

    getEnergyTooltip(cell, rowIndex, colIndex) {
      if (cell === 0) return "";

      const source = this.sources?.[rowIndex] || "Unknown";
      const time = this.formatOrderingPlanTime(colIndex);
      const consumption = this.consumptionMatrix?.[rowIndex]?.[colIndex] ?? 0;
      let label = "";

      if (cell === 1) {
        label = "Reserved energy";
      } else if (cell === 2) {
        label = "Sold energy";
      } else if (cell === 3) {
        label = "Paid energy and ACK from seller";
      }

      return `${label} | Source: ${source} | Consumption: ${consumption.toFixed(
        2
      )} kWh`;
    },

    async fetchAgentDetails() {
      if (!this.seller || !this.seller.agentUrl) return; // pridaj kontrolu, či existuje seller a agentUrl
      try {
        const url = `${this.seller.agentUrl}/rest/details`;
        const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: "", session_id: 0 }),
        });

        if (!response.ok)
          return;

        const data = await response.json();
        if (data && data.energyToCO2) {
          this.carbonEmissionFactor = parseFloat(data.energyToCO2);
        }
      } catch (error) {
      }
    },

    debounce(func, delay) {
      let timeout;
      return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), delay);
      };
    },

    async fetchTradingAnalytics() {
      try {
        this.loadingGraphData=true; //begin of loading data
        const now = Math.floor(Date.now() / 1000);
        const oneDay = 24 * 3600;
        const historyUrl = `${this.seller.agentUrl}/rest/energyHistory`;
        const predictionUrl = `${this.seller.agentUrl}/rest/energyStatePrediction`;
        let endTimestamp = now;

        let from, granularity, labelFormat;

        switch (this.graphTimeRange) {
          case "Custom":
            from = Math.floor(new Date(this.customRangeStart).setHours(0, 0, 0, 0) / 1000);
            const end = Math.floor(new Date(this.customRangeEnd).setHours(0, 0, 0, 0) / 1000);
            const duration = end - from;

            if (duration <= 0) {
              return;
            } else if (duration <= oneDay) {
              granularity = "block";
              labelFormat = "DD.MM HH:mm";
            } else if (duration <= 30 * oneDay) {
              granularity = "day";
              labelFormat = "DD MMM";
            } else {
              granularity = "month";
              labelFormat = "MMM YYYY";
            }

            endTimestamp = end;

            // 💾 Uchovávanie rozsahu (už existuje, nič sa nemení)
            break;

          case "Day":
            this.customRangeStart = moment().startOf("day").toDate();
            this.customRangeEnd = moment().endOf("day").toDate();
            from = Math.floor(this.customRangeStart.getTime() / 1000);
            endTimestamp = Math.floor(this.customRangeEnd.getTime() / 1000);
            granularity = "block";
            labelFormat = "DD.MM HH:mm";
            break;

          case "Week":
            this.customRangeStart = moment().subtract(7, "days").startOf("day").toDate();
            this.customRangeEnd = moment().endOf("day").toDate();
            from = Math.floor(this.customRangeStart.getTime() / 1000);
            endTimestamp = Math.floor(this.customRangeEnd.getTime() / 1000);
            granularity = "day";
            labelFormat = "DD MMM";
            break;

          case "Month":
            this.customRangeStart = moment().subtract(30, "days").startOf("day").toDate();
            this.customRangeEnd = moment().endOf("day").toDate();
            from = Math.floor(this.customRangeStart.getTime() / 1000);
            endTimestamp = Math.floor(this.customRangeEnd.getTime() / 1000);
            granularity = "day";
            labelFormat = "DD MMM";
            break;

          case "Year":
          default:
            this.customRangeStart = moment().startOf("year").toDate();
            this.customRangeEnd = moment().endOf("day").toDate();
            from = Math.floor(this.customRangeStart.getTime() / 1000);
            endTimestamp = Math.floor(this.customRangeEnd.getTime() / 1000);
            granularity = "month";
            labelFormat = "MMM";
            break;
        }


        // === 🟢 SHOW FINANCIAL ===
        const response = await fetch(`${this.seller.agentUrl}/rest/history`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            startTimestamp: from,
            endTimestamp,
            granularity,
            type: "default",
          }),
        });

        const json = await response.json();
        const data = json.result || [];

        const totals = {};
        const labelSet = new Set();

        let totalSoldFinancial = 0; // 💰 súčet predajov v €

        // spracuj všetky položky
        data.forEach((entry) => {
          const rawTimestamp = entry.timestamp || entry.blockStart || entry.day || entry.month;
          const date = new Date(
            typeof rawTimestamp === "string"
              ? rawTimestamp
              : rawTimestamp * (String(rawTimestamp).length === 10 ? 1000 : 1)
          );
          const label = moment(date).format(labelFormat);

          const eurValue = parseFloat(this.convertFETToEUR(this.toTestFET(entry.price))) || 0;
          if (!totals[label]) totals[label] = 0;
          totals[label] += eurValue;
          totalSoldFinancial += eurValue; // ➕ pripočítaj k celkovému súčtu
          labelSet.add(label);
        });

        this.soldEnergyFinancial = +totalSoldFinancial.toFixed(2);

        // === 📊 Ak je showFinancial, vytvor graf a skonči ===
        if (this.showFinancial) {
          const labels = Array.from(labelSet).sort((a, b) =>
            moment(a, labelFormat).diff(moment(b, labelFormat))
          );
          const values = labels.map((label) => +totals[label].toFixed(4));

          this.chartDataYear = {
            labels,
            datasets: [
              {
                label: "Price (€)",
                backgroundColor: "rgba(0, 200, 83, 0.7)",
                data: values,
              },
            ],
          };

          this.loadingGraphData = false;
          return;
        }


        // === 🔵 ENERGY HISTORY + PREDICTION ===
        const historyRes = await fetch(historyUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            startTimestamp: from,
            endTimestamp,
            granularity,
            type: "",
          }),
        });

        const historyJson = await historyRes.json();
        const historyData = historyJson.result || [];

        let predictionList = [];
        if (this.graphTimeRange === "Day") {
          const predictionRes = await fetch(predictionUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ stopUnixTimestamp: now + 2 * oneDay }),
          });
          const predictionJson = await predictionRes.json();
          predictionList = predictionJson.predictionList || [];
        }

        const allData = [];

        // === 🕘 Historical
        historyData.forEach((entry) => {
          const rawDate = entry.time
            ? new Date(entry.time * 1000)
            : new Date(entry.day || entry.month);
          const label = moment(rawDate).format(labelFormat);

          allData.push({
            label,
            produced: entry.generatedPower || 0,
            stored: entry.energy || 0,
            sold: entry.soldEnergy || 0,
            predicted: null,
          });
        });

        // === 🔮 Prediction
        if (this.graphTimeRange === "Day") {
          predictionList.forEach((entry) => {
            const label = moment(entry.timestamp * 1000).format(labelFormat);
            allData.push({
              label,
              produced: null,
              stored: null,
              sold: null,
              predicted: entry.availableEnergy || 0,
            });
          });
        }

        // === 🧼 Sort and group
        const combinedMap = new Map();

        allData.forEach((d) => {
          if (!combinedMap.has(d.label)) {
            combinedMap.set(d.label, { ...d });
          } else {
            const prev = combinedMap.get(d.label);
            combinedMap.set(d.label, {
              label: d.label,
              produced: (prev.produced || 0) + (d.produced || 0),
              stored: (prev.stored || 0) + (d.stored || 0),
              sold: (prev.sold || 0) + (d.sold || 0),
              predicted: d.predicted !== null ? d.predicted : prev.predicted,
            });
          }
        });

        const sorted = Array.from(combinedMap.values()).sort((a, b) =>
          moment(a.label, labelFormat).diff(moment(b.label, labelFormat))
        );
        // 💾 Ulož prvú hodnotu stored energy ako počiatočný stav
        const firstStored = sorted.find(d => typeof d.stored === 'number');
        this.initialStoredEnergy = firstStored ? firstStored.stored : 0;

        const labels = sorted.map((d) => d.label);
        const produced = sorted.map((d) => d.produced);
        const stored = sorted.map((d) => d.stored);
        const sold = sorted.map((d) => d.sold);
        const predicted = sorted.map((d) => d.predicted);

        const emissionData = sold.map(
          (soldEnergy) => soldEnergy * this.carbonEmissionFactor
        );

        this.emissionSourceData = {
          labels,
          datasets: [
            {
              label: "Emissions (kg CO₂)",
              backgroundColor: "rgba(244, 67, 54, 0.6)",
              borderColor: "rgba(244, 67, 54, 1)",
              data: emissionData,
              fill: false,
              tension: 0.3,
            },
          ],
        };
        
        this.chartDataYear = {
          labels,
          datasets: [
            {
              label: "Produced energy (kWh)",
              borderColor: "rgb(244, 208, 63)",
              backgroundColor: "rgb(247, 217, 99)",
              data: produced,
              fill: false,
              tension: 0.3,
            },
            {
              label: "Stored energy (kWh)",
              borderColor: "rgb(39, 174, 96)",
              backgroundColor: "rgb(85, 243, 151)",
              data: stored,
              fill: false,
              tension: 0.3,
            },
            {
              label: "Sold energy (kWh)",
              borderColor: "rgb(100, 150, 238)",
              backgroundColor: "rgba(2, 125, 255, 0.2)",
              data: sold,
              fill: false,
              tension: 0.3,
            },
            ...(this.graphTimeRange === "Day"
              ? [
                  {
                    label: "Predicted energy (kWh)",
                    borderColor: "rgba(120, 190, 120, 1)",
                    backgroundColor: "rgba(144, 238, 144, 0.1)",
                    borderDash: [5, 5],
                    data: predicted,
                    fill: false,
                    tension: 0.3,
                  },
                ]
              : []),
          ],
        };
      
        // calculate variables for stats
        this.producedEnergy = produced.reduce((sum, val) => sum + (val || 0), 0);
        this.storedEnergy = stored.reduce((acc, current, index) => {
          if (index === 0) return acc;
          const prev = stored[index - 1] || 0;
          const diff = current - prev;
          if (diff > 0) acc += diff; // len pozitívne rozdiely
          return acc;
        }, 0);
        this.soldEnergy = sold.reduce((sum, val) => sum + (val || 0), 0);
        this.efficientlyUsedEnergy = this.soldEnergy + this.storedEnergy;
        this.producerCapacity=this.producedEnergy+this.storedEnergy + this.initialStoredEnergy;        
        this.wastedEnergy=this.producerCapacity-this.soldEnergy-this.storedEnergy;
        this.loadingGraphData=false; //finished loading data
      } catch (error) {}
    },

    async updateSmartTips() {
      const oneDay = 24 * 3600;
        let now = Math.floor(Date.now() / 1000);
        let from;
        let granularity;
        let labelFormat;
      
        switch (this.graphTimeRange) {
          case "Custom":
            from = Math.floor(new Date(customRangeStart).setHours(0, 0, 0, 0) / 1000);
            const end = Math.floor(new Date(customRangeEnd).setHours(0, 0, 0, 0) / 1000);
            const duration = end - from;
            if (duration <= 0) {
              return;
            }
            else if (duration <= oneDay) {
              granularity = "block";
              labelFormat = "DD.MM HH:mm";
            } else if (duration <= 30 * oneDay) {
              granularity = "day";
              labelFormat = "DD MMM";
            } else {
              granularity = "month";
              labelFormat = "MMM YYYY";
            }
            now = end;
            break;
          case "Day":
            from = Math.floor(moment().startOf('day').valueOf() / 1000);
            granularity = "block"; 
            labelFormat = "HH:00";
            break;
          case "Week":
            from = moment().subtract(7, "days").unix();
            granularity = "day";
            labelFormat = "DD MMM";
            break;
          case "Month":
            from = moment().subtract(30, "days").unix();
            granularity = "day";
            labelFormat = "DD MMM";
            break;
          case "Year":
          default:
            from = moment().startOf("year").unix();
            granularity = "month";
            labelFormat = "MMM";
            break;
        }
    
        //GET SMART TIPS FROM LLM
        try {
          const response = await fetch(`${this.seller.agentUrl}/rest/smartTips`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              startTimestamp: from,
              endTimestamp: now,
              granularity,
              analysisType: ""
            }),
          });

          const data = await response.json();
          this.smartTipsText = data.text;
          
        } catch (err) {
        }
    },

    toAttoFET(fet) {
      return BigInt(Math.round(fet * 1e18));
    },
    toTestFET(atto) {
      return (Number(atto) / 1e18).toFixed(5);
    },

    convertUSDToFET(usdValue) {
      if (!this.fetPriceUSD || !usdValue) return "-";
      return (usdValue / this.fetPriceUSD).toFixed(4); // výstup v TESTFET
    },
    convertFETToUSD(fetValue) {
      if (!this.fetPriceUSD || isNaN(fetValue)) return "0.0000";
      return (fetValue * this.fetPriceUSD).toFixed(4);
    },
    convertFETToEUR(fetValue) {
      if (!this.fetPriceUSD || isNaN(fetValue)) return "0.0000";
      return fetValue * this.fetPriceUSD;
    },

    async fetchFETPrice() {
      try {
        const response = await fetch(
          "https://www.okx.com/api/v5/market/ticker?instId=FET-EUR"
        );
        const data = await response.json();
        if (data?.data?.[0]?.last) {
          this.fetPriceUSD = parseFloat(data.data[0].last);
        }
      } catch (error) {}
    },

    copyToClipboard(value, event) {
      navigator.clipboard.writeText(value).then(() => {
        const tooltip = document.createElement("div");
        tooltip.innerText = "Copied!";
        tooltip.className = "copy-tooltip";

        // Pozícia podľa kurzora
        tooltip.style.top = `${event.clientY + 10}px`;
        tooltip.style.left = `${event.clientX + 10}px`;

        document.body.appendChild(tooltip);

        // Po 1.5s tooltip zmizne
        setTimeout(() => {
          document.body.removeChild(tooltip);
        }, 1000);
      });
    },

    onPriceInput(value) {
      if (this.useFixedPrice) {
        // ✅ Nahradiť čiarku za bodku
        const sanitized = String(value).replace(',', '.');

        const atto = this.toAttoFET(parseFloat(sanitized) || 0);
        this.energyToPriceRate = atto;

        // ✅ Update aj `priceInputValue` späť (v prípade, že užívateľ napísal čiarku)
        this.priceInputValue = parseFloat(sanitized).toFixed(5);

        this.debouncedSendPriceUpdate(atto);
      }
    },

    handlePriceBlur() {
      if (!this.useFixedPrice) return;

      const parsed = parseFloat(this.priceInputValue);
      if (!isNaN(parsed)) {
        const atto = this.toAttoFET(parsed);
        this.energyToPriceRate = atto;
        this.debouncedSendPriceUpdate(atto);
      }
    },

    adjustPriceStep(direction) {
      if (!this.useFixedPrice) return;

      const current = parseFloat(this.priceInputValue) || 0;
      const step = 0.001;
      const updated = Math.max(0, current + direction * step);

      this.priceInputValue = updated.toFixed(5);
      this.handlePriceBlur();
    },

    async fetchSpotPrice() {
      try {
        const url = `${this.seller.agentUrl}/rest/resources`;
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            text: "",
            session_id: 0,
          }),
        });

        const data = await response.json();

        if (data.currentPriceKWh !== undefined) {
          this.spotElectricityPrice = data.currentPriceKWh;
          this.connectedClients = data.activeClients;
          this.connectedClientList = data.activeClientList;

          this.connectedClientList = this.connectedClientList
            .map((agentAddress) => {
              const customer = customerByAgent[agentAddress];
              return customer ? customer.token.split("|")[0] : null;
            })
            .filter((token) => token !== null);

          if (data.pricing === 0) {
            this.useFixedPrice = false;

            this.$nextTick(() => {});
          } else {
            this.useFixedPrice = true;
            this.energyToPriceRate = data.pricing;
          }

          this.euaAmount = data.co2Tokens;
          this.energyTokenCount = data.energyTokens;
          this.maxEnergy = data.maxEnergyCapacity;
        }
      } catch (error) {
      }
    },

    async sendPriceUpdate(price) {
      try {
        const url = `${this.seller.agentUrl}/rest/setPricing`;

        const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ price: price.toString() }), // priamo v TESTFET
        });
      } 
      catch (error) {
      }
    },

    checkTimeAndRefresh() {
      const currentDate = new Date();
      const minutes = currentDate.getMinutes();

      // Check if the current time is 0, 15, 30, or 45 minutes past the hour
      if ([0, 15, 30, 45].includes(minutes)) {
        this.fetchData(); //refresh energy ordering table
      }
    },
    async updateData() {
      const seller = sellerByPath[this.$route.path];

      if (seller) {
        this.seller = seller;
        this.address = seller.walletAddress;
        this.agAddress = seller.agentAddress;
        await this.fetchSpotPrice();
      } else {
        this.address = "Unknown";
        this.agAddress = "Unknown";
        this.balance = BigInt(0);
        this.rawTransactions = [];
        this.transactionsWithBalance = [];
        return;
      }

      // 🎲 Randomize values
      this.chargingPower = (Math.random() * (20 - 5) + 5).toFixed(1);
      this.dischargingPower = (Math.random() * (6 - 2) + 2).toFixed(1);
      this.storedEnergy = (Math.random() * (20 - 10) + 10).toFixed(1);

      this.currentPage = 1;
      await this.getTransactions();
      this.calculateHistoricalBalances();
    },

    async fetchEUAPrice() {
      try {
        const url = `${baseUrl}/agent15/rest/marketPrices`; //agentMarket url
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            text: "",
            session_id: 0,
          }),
        });

        const data = await response.json();

        if (data.co2Price !== undefined) {
          this.euaCurrentPrice = data.co2Price;
        }
        

      } catch (error) {
      }
    },

    async getBalance() {
      try {
        const query = `
      query GetWalletBalance {
        balances(
          where: { address: { _eq: "${this.address}" } }
        ) {
          balance
          denom
        }
      }
    `;

        const response = await fetch(`${baseUrl}/graphql`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            query,
            operationName: "GetWalletBalance",
          }),
        });

        const result = await response.json();

        const balances = result?.data?.balances || [];
        const fetBalance = balances.find((coin) => coin.denom === "atestfet");

        this.balance = fetBalance ? BigInt(fetBalance.balance) : BigInt(0);
      } catch (error) {
        this.balance = BigInt(0);
      }
    },

    async getTransactions() {
      try {
        const query = `
      query GetTransactions {
        sent: transfers(
          where: {to_address: {_eq: "${this.address}"}}
          order_by: {height: desc}
          limit: 20
        ) {
          amount
          denom
          from_address
          to_address
          tx_hash
        }
        received: transfers(
          where: {to_address: {_eq: "${this.address}"}}
          order_by: {height: desc}
          limit: 20
        ) {
          amount
          denom
          from_address
          to_address
          tx_hash
        }
      }
    `;

        const response = await fetch(`${baseUrl}/graphql`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ query }),
        });

        const { data } = await response.json();
        const sent = data.sent || [];
        const received = data.received || [];

        // Format basic transaction object
        const formatTx = (tx, type) => ({
          txhash: tx.tx_hash,
          from: tx.from_address,
          to: tx.to_address,
          fromName: tx.from_address,
          toName: tx.to_address,
          operation: type === "sent" ? "Outgoing" : "Incoming",
          amount: tx.amount ?? "0",
          timestamp: null, // Will be filled later
          balanceAfterTx: "?",
        });

        // Combine and deduplicate
        const all = [
          ...sent.map((tx) => formatTx(tx, "sent")),
          ...received.map((tx) => formatTx(tx, "received")),
        ];

        const unique = all.filter(
          (tx, index, self) =>
            index === self.findIndex((t) => t.txhash === tx.txhash)
        );

        // Fetch timestamps in parallel
        const timestampFetches = unique.map((tx) =>
          fetch(`${baseUrl}/graphql`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              query: `
            query GetTransactionTime {
              transactions(
                where: {hash: {_eq: "${tx.txhash}"}}
                limit: 1
              ) {
                time
              }
            }
          `,
            }),
          })
            .then((res) => res.json())
            .then((res) => {
              const utcTime = res?.data?.transactions?.[0]?.time || null;

              if (!utcTime) return { hash: tx.txhash, time: null };
              const timezoneOffset = -1 * new Date().getTimezoneOffset(); //-120 ; I need to add 120

              const date = new Date(utcTime);
              date.setMinutes(date.getMinutes() + timezoneOffset);

              const localTime = `${date.getFullYear()}-${String(
                date.getMonth() + 1
              ).padStart(2, "0")}-${String(date.getDate()).padStart(
                2,
                "0"
              )} ${String(date.getHours()).padStart(2, "0")}:${String(
                date.getMinutes()
              ).padStart(2, "0")}:${String(date.getSeconds()).padStart(
                2,
                "0"
              )}`;

              return { hash: tx.txhash, time: localTime };
            })
            .catch(() => ({ hash: tx.txhash, time: null }))
        );

        const timestampResults = await Promise.all(timestampFetches);

        // Map hash → time
        const timestampMap = {};
        for (const { hash, time } of timestampResults) {
          timestampMap[hash] = time;
        }

        // Attach timestamps
        unique.forEach((tx) => {
          tx.timestamp = timestampMap[tx.txhash] || null;
        });

        // Sort transactions by timestamp descending
        this.rawTransactions = unique.sort(
          (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
        );

        // Pagination
        //this.paginatedTransactions = this.rawTransactions.slice(0, this.itemsPerPage);
        //this.totalPages = Math.ceil(this.rawTransactions.length / this.itemsPerPage);
        this.currentPage = 1;
      } catch (error) {
        //console.error("Error fetching transactions:", error);
        this.rawTransactions = [];
        //this.paginatedTransactions = [];
        //this.totalPages = 0;
        this.currentPage = 1;
      }
    },

    calculateHistoricalBalances() {
      const result = [];
      let runningBalance = this.balance;

      for (const tx of this.rawTransactions) {
        const from = this.getSender(tx);
        const to = this.getRecipient(tx);
        const amount = this.getAmountRaw(tx);
        const operation = this.getOperation(from, to);
        const fromName =
          customerByWallet[from]?.name ||
          sellerByWallet[from]?.name ||
          "Unknown";
        const toName =
          customerByWallet[to]?.name || sellerByWallet[to]?.name || "Unknown";

        result.push({
          txhash: tx.txhash,
          timestamp: tx.timestamp,
          from,
          to,
          operation,
          fromName,
          toName,
          amount,
          balanceAfterTx: runningBalance,
        });

        if (operation.startsWith("Incoming")) {
          runningBalance -= amount;
        } else if (operation.startsWith("Outgoing")) {
          runningBalance += amount;
        }
      }

      this.transactionsWithBalance = result;
    },

    getSender(tx) {
      return tx.from || "–";
    },

    getRecipient(tx) {
      return tx.to || "–";
    },

    getAmountRaw(tx) {
      try {
        return BigInt(tx.amount);
      } catch {
        return BigInt(0);
      }
    },

    getOperation(from, to) {
      if (from === this.address && to !== this.address) {
        return "Outgoing";
      } else if (to === this.address && from !== this.address) {
        return "Incoming";
      } else if (from === this.address && to === this.address) {
        return "Self Transfer";
      } else if (from === "–" && to !== "–") {
        return "Incoming (unknown sender)";
      } else if (to === "–" && from !== "–") {
        return "Outgoing (unknown recipient)";
      } else {
        return "Other";
      }
    },

    async fetchData() {
      const hours = 12;
      const now = new Date();

      let roundedDate;

      if (this.overrideOrderingStartUnix) {
        // ✅ použijeme timestamp z kalendára
        roundedDate = new Date(this.overrideOrderingStartUnix * 1000);
      } else {
        roundedDate = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          now.getHours(),
          0,
          0,
          0
        );
        roundedDate.setHours(roundedDate.getHours() - 2); // posun o 2h späť
      }

      if (!this.orderingSelectedDate) {
        this.orderingSelectedDate = new Date(roundedDate);
        const hh = String(this.orderingSelectedDate.getHours()).padStart(2, '0');
        this.orderingSelectedTime = `${hh}:00`;
      }

      const startUnixTimestamp = Math.floor(roundedDate.getTime() / 1000);
      this.overrideOrderingStartUnix = null; // reset na ďalšie použitie

      this.seller = sellerByPath[this.$route.path];
      if (!this.seller) return;

      const url = `${this.seller.agentUrl}/rest/energyorderingtable`;
      const requestData = {
        startUnixTimestamp,
        hours,
      };

      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        });

        const data = await response.json();
        if (
          data &&
          Array.isArray(data.sources) &&
          Array.isArray(data.matrix) &&
          typeof data.startUnixTimestamp === "number"
        ) {
          this.sources = data.sources.map((agentAddr) => {
            const customer = customerByAgent[agentAddr];
            return customer ? customer.name : agentAddr;
          });

          this.matrix = data.matrix;
          this.consumptionMatrix = data.consumptionMatrix;
          this.startTimestamp = data.startUnixTimestamp;
          this.columnCount = data.matrix[0]?.length || 0;
          this.energyOrderingDataOK = true;
        } else {
          this.energyOrderingDataOK = false;
        }

        const now2 = Math.floor(Date.now() / 1000);
        const minutesSinceStart = Math.floor(
          (now2 - data.startUnixTimestamp) / 60
        );
        this.currentColumnIndex = Math.floor(minutesSinceStart / 15);
      } catch (error) {
        this.energyOrderingDataOK = false;
      }
    },

    formatOrderingPlanTime(index) {
      const minutesOffset = index * 15;
      const ts = (this.startTimestamp + minutesOffset * 60) * 1000;
      const date = new Date(ts);
      const hours = date.getHours();
      return date.getMinutes() === 0 ? hours : "";
    },
    shouldShowHourLabel(index) {
      return index % 4 === 0;
    },
    getColumnClass(index) {
      const classes = [];

      if (index % 4 === 0) classes.push("hour-border");
      else classes.push("quarter-border");

      if (index === this.columnCount - 1) classes.push("last-column-border");

      // Add highlight if this is the current 15-min timeframe
      if (index === this.currentColumnIndex) classes.push("current-time");

      return classes.join(" ");
    },

    getCellClass(index, value) {
      const classes = [];

      if (value) {
        switch (value) {
          case 1:
            classes.push("active-cell");
            break;
          case 2:
            classes.push("active-cell-paid");
            break;
          case 3:
            classes.push("active-cell-paid-ack");
            break;
          default:
            classes.push("active-cell");
        }
      }

      if (index % 4 === 0) classes.push("hour-border");
      else classes.push("quarter-border");

      if (index === this.columnCount - 1) classes.push("last-column-border");

      // Add highlight if this is the current 15-min timeframe
      if (index === this.currentColumnIndex) classes.push("current-time");

      classes.push("row-border");

      return classes.join(" ");
    },

    formatTime(iso) {
      const date = new Date(iso);
      return date.toLocaleString();
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
  },
};
</script>

<style>

.bar {
  height: 100%;
}

.section-labels.two-columns {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section-labels.two-columns .left {
  text-align: left;
}

.section-labels.two-columns .right {
  text-align: right;
}

.section-labels.three-columns {
  display: grid;
  grid-template-columns: 1fr auto 1fr; /* left, center, right */
  align-items: center;
}

.section-labels.three-columns .left {
  text-align: left;
}

.section-labels.three-columns .center {
  text-align: center;
}

.section-labels.three-columns .right {
  text-align: right;
}

/* Shared container styles remain the same */
.progress-bar-container {
  display: flex;
  width: 100%;
  height: 20px;
  border-radius: 10px;
  overflow: hidden;
  background-color: #e5e7eb;
}

.sold-blue {
  background-color: rgb(100, 150, 238); /* blue */
}

.wasted-gray {
  background-color: rgba(119, 119, 119, 0.4); /* lighter yellow */
}

.stored-green {
  background-color: #16a34a; /* green */
}

.produced-yellow {
  background-color: #facc15; /* yellow */
}

.energyflow-card-content {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding: 1rem;
}

.energyflow-container {
  position: relative;
  width: 100%;
  max-width: 900px;
  aspect-ratio: 2.5 / 1;
  margin-left: 8%;
  margin-top: -5%;
}

.node {
  position: absolute;
  transform: translate(-50%, -50%);
  width: auto;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none; /* ikona nebude blokovať SVG */
}

.node svg {
  width: 80px;
  height: 80px;
  pointer-events: auto; /* ak chceš klikateľnosť ikony */
}

.node-label {
  margin-top: 95px;
  font-size: 16px;
  color: #444;
  line-height: 1.2;
  white-space: nowrap;
}

.node-label-top {
  margin-bottom: 85px; /* rovnaká vzdialenosť ako pôvodne hore */
  margin-top: -25px;
}

/* Presné pozície – optimalizované aj pre mobil */
.battery {
  left: 10%;
  top: 33%;
}
.solar {
  left: 10%;
  top: 83%;
}
.energy-seller {
  left: 44%;
  top: 59%;
}

.grid {
  left: 82%;
  top: 59%;
}

.energy-lines {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.price-settings {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
/* Ensure the form takes the full width */
form {
  display: flex;
  width: 100%;
  gap: 8px; /* Add space between the input and button */
  align-items: center; /* Vertically center the input and button */
}

/* Input field takes all available width and adjusted height */
.input-field {
  flex-grow: 1;
  height: 40px; /* Set a fixed height for better alignment with the button */
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
  line-height: 24px; /* Ensure text is vertically centered */
}

/* Optional style overrides */
.bg-blue-600 {
  background-color: #4c8bf5;
  text-align: right;
  padding-right: 10px;
}

.bg-gray-300 {
  background-color: #f3f3f3;
  padding-left: 10px;
}

.text-white {
  color: white;
}

.text-gray-800 {
  color: #333;
}

button {
  transition: background-color 0.3s;
  height: 40px; /* Set a fixed height to match the input box */
}

button:disabled {
  cursor: not-allowed;
  background-color: #b5b5b5;
}

/* Card Content */
.chat-card-content {
  display: flex;
  flex-direction: column;
  height: calc(100% - 120px); /* Adjust the height to fit within the card */
  padding-bottom: 20px; /* Ensure space for the input area */
}

/* Scrollable chat messages container */
.chat-messages-container {
  flex-grow: 1;
  overflow-y: auto;
  padding-right: 8px;
}

.slider-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.price-slider {
  flex-grow: 1;
}

.slider-label {
  width: 40px;
  text-align: center;
  font-size: 14px;
  color: #555;
}

.slider-value {
  margin-top: 8px;
  font-size: 16px;
  color: #333;
}

/* energy ordering plan */
.energy-table {
  border-collapse: collapse;
  border: none;
  width: max-content;
  min-width: 100%;
}

.energy-table th,
.energy-table td {
  width: 20px;
  height: 25px;
  text-align: center;
  padding: 0;
  position: relative;
  border-top: 2px solid black;
  border-bottom: 2px solid black;
}

/* Light gray borders for 15-minute quarters */
.energy-table .quarter-border {
  border-left: 1px solid #ddd;
}

/* Black border every full hour */
.energy-table .hour-border {
  border-left: 2px solid black;
}

/* Border for last column and full table bottom */
.energy-table .last-column-border {
  border-right: 2px solid black;
}

.energy-table .row-border {
  border-top: 1px solid black;
}

.source-name {
  min-width: 130px;
  text-align: left;
  padding-left: 5px;
  background-color: #f8f8f8;
  font-weight: bold;
  border-right: 2px solid black;
  border-top: 1px solid black;
}

/* Green active cell but smaller with white padding */
.active-cell {
  background-color: transparent;
  position: relative;
}

.active-cell::after {
  content: "";
  display: block;
  width: 17px;
  height: 17px;
  margin: auto;
  background-color: rgb(240, 48, 48);
  border: 1px solid white;
  border-radius: 2px;
  pointer-events: none;
}

.active-cell-paid::after {
  content: "";
  display: block;
  width: 17px;
  height: 17px;
  margin: auto;
  background-color: #ed6b0e;
  border: 1px solid white;
  border-radius: 2px;
  pointer-events: none;
}

.active-cell-paid-ack::after {
  content: "";
  display: block;
  width: 17px;
  height: 17px;
  margin: auto;
  background-color: #4caf50;
  border: 1px solid white;
  border-radius: 2px;
  pointer-events: none;
}

/* Current time highlight */
.current-time {
  background-color: #ffff99 !important;
}

.date-footer td {
  border-top: 2px solid black;
  border-right: 2px solid black;
  border-bottom: 2px solid black;

  text-align: center;
  background-color: #f9f9f9;
  font-weight: bold;
  padding: 4px 6px;
}

.date-cell {
  border-right: 2px solid black;
  background-color: #f9f9f9;
}

.date-footer .date-empty-cell {
  background-color: white !important;
  border: none !important; /* Reset all borders */
  border-top: 2px solid black !important;
  border-right: 2px solid black !important;
}

.energy-table tbody td:first-child {
  border-left: 2px solid black; /* or whatever color/thickness you need */
}

.energy-table thead tr:first-child th:first-child {
  border-right: 2px solid black;
  border-top: none !important;
}

.energy-table thead tr:first-child th:last-child {
  border-right: 2px solid black;
}

.scroll-table {
  overflow-x: auto; /* Enables horizontal scroll */
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on mobile */
  width: 100%; /* Ensures it takes up the full width of its container */
}

.switch {
  position: relative;
  display: inline-block;
  width: 42px;
  height: 22px;
}

.switch input {
  display: none;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 22px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #4caf50;
}

input:checked + .slider:before {
  transform: translateX(20px);
}

.selected-mode {
  font-weight: bold;
  color: #2196f3;
}
.price-input-right {
  width: 50%;
  margin-left: auto;
  margin-right: 0;
  margin-bottom: 0;
  margin-top: 0;
  padding-bottom: 0;
}

.price-input-right input {
  text-align: right;
  font-size: 16px;
  padding: 6px 10px;
  width: 100px;
  border: 1px solid #ccc; 
  border-radius: 4px;
}

.wallet-name {
  cursor: pointer;
  position: relative;
  display: inline-block;
  font-weight: 500;
  color: #1f2f40;
}
.copy-tooltip {
  position: fixed;
  background-color: white;
  color: black;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 200;
  pointer-events: none;
  z-index: 9999;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  transition: opacity 0.3s ease;
}
.spot-footer {
  display: flex;
  justify-content: flex-end;
  padding: 0 8px;
  font-size: 14px;
  color: #555;
  text-align: left;
  width: 100%;
}

/* Toggle switch layout */
.toggle-switch {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.label-text {
  font-weight: bold;
  font-size: 14px;
  color: #1f2f40;
}

/* Actual switch */
.switch {
  position: relative;
  display: inline-block;
  width: 46px;
  height: 24px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #4caf50;
  transition: 0.4s;
  border-radius: 34px;
}

.slider::before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #f9a825;
}

input:checked + .slider::before {
  transform: translateX(22px);
}

.tabs-toggle-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  gap: 1rem;
}

.stats-container {
  justify-content: space-around;
}

.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 1rem;
  gap: 10px;
}

.tab-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex-grow: 1;
  padding: 2px;
  position: relative;
  align-items: center;
  z-index: 10000;
}

.custom-date-picker {
  border-radius: 6px;

  .md-layout-item {
    max-width: 180px;
  }
}

.md-card .md-card-content {
  line-height: 1.55em;
}

.custom-calendar-popup {
  position: absolute;
  background: #fff;
  border: 1px solid #ccc;
  padding: 12px;
  border-radius: 6px;
  z-index: 99999;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);
  font-family: Roboto, sans-serif;
  width: auto;
  min-width: 280px;
}

.calendar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 500;
  margin-bottom: 6px;
  text-align: center;
  color: #212529;
}

.month-nav {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  padding: 4px 10px;
  font-weight: 500;
  color: #212529;
  border-radius: 4px;

  &:hover {
    background-color: #eee;
  }
}

.month-name {
  flex: 1;
  text-align: center;
  white-space: nowrap;
  font-size: 14px;
  color: #212529;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 36px);
  gap: 2px;
}

.day-name {
  font-weight: 500;
  text-align: center;
  font-size: 12px;
  color: #999;
}

.calendar-day {
  width: 36px;
  height: 36px;
  text-align: center;
  line-height: 36px;
  cursor: pointer;
  border-radius: 50%;
  font-size: 14px;
  transition: background 0.2s;

  &:hover {
    background-color: #eee;
  }
}

.calendar-day.today {
  border: 1px solid #4ba64f;
}

.calendar-day.selected {
  background: #4ba64f;
  color: white;
  font-weight: 500;
}

.calendar-day.inRange {
  background: lighten(#4ba64f, 30%);
  color: #000;
}

.timestamp-inline {
  font-size: 14px;
  color: #333;
  margin-left: 12px;
  white-space: nowrap;
}
.calendar-day.inRange {
background: #a5d6a7;
}
.calendar-day.today {
border: 1px solid #4caf50;
}

.time-picker {
  padding: 4px 6px;
  height: 30px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.price-input-wrapper {
  width: 50%;
  margin-left: auto;
  text-align: right;
}

.custom-price-input {
  width: 100%;
  text-align: right;
  padding: 6px 10px;
  border: 0px;
  border-radius: 4px;
  font-size: 16px;
  font-family: Roboto, sans-serif;
}

.overview-card-content {
  padding: 16px;
}

.overview-section {
  display: flex;
  flex-direction: column;
  font-size: 14px;
}

.overview-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  line-height: 1.8em;
}

.overview-row .label {
  font-weight: 500;
  color: #333;
}

.overview-row .value {
  font-weight: 500;
  color: #000;
  display: flex;
  justify-content: flex-end;
  tab-size: 4;
  white-space: pre; /* preserves spacing */
}

.overview-row .total {
  font-size: 15px;
  font-weight: 600;
}

.overview-row.total .label {
  font-weight: 700;
}

.overview-align {
  display: inline-block;
  min-width: 100px; /* enough for the widest number */
  text-align: right;
}
.overview-unit {
  margin-left: 4px;
  color: #000;
}

.overview-divider {
  border: none;
  border-top: 1px solid #ccc;
  margin: 2px 0;
}

.overview-subsection-title {
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  color: #555;
  margin-top: 8px;
}

button {
  transition: background-color 0.3s;
  height: 40px; /* Set a fixed height to match the input box */
}

button:disabled {
  cursor: not-allowed;
  background-color: #b5b5b5;
}

/* Card Content */
.chat-card-content {
  display: flex;
  flex-direction: column;
  height: calc(100% - 120px); /* Adjust the height to fit within the card */
  padding-bottom: 20px; /* Ensure space for the input area */
}

/* Scrollable chat messages container */
.chat-messages-container {
  flex-grow: 1;
  overflow-y: auto;
  padding-right: 8px;
}

.smart-tips-scrollable {
  max-height: 400px; 
  overflow-y: auto;
  overflow-x: hidden;
}

.smart-tips-text {
  white-space: pre-wrap;
}


@media (max-width: 768px) {
  .overview-align {
    min-width: 50px;
  }
  
  .tabs-toggle-container > div:last-of-type {
    width: 100%;
    order: 2;
    display: grid !important;
    grid-template-columns: 1fr;
    gap: 10px;
    margin-left: 0 !important;
  }

  .tabs-toggle-container > div:last-of-type .preferred-seller-container {
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .tabs-toggle-container > div:last-of-type .preferred-seller-select,
  .tabs-toggle-container > div:last-of-type .md-field,
  .tabs-toggle-container > div:last-of-type .md-select {
    width: 100% !important;
    min-width: 0 !important;
    display: block !important;
  }

  /* jemné zmenšenie, nech sa to pekne zmestí */
  .tabs-toggle-container > div:last-of-type .md-select {
    padding: 4px 10px !important;
    font-size: 14px !important;
    min-height: 36px !important;
  }

  .battery,
  .solar {
    left: 10%;
  }

  .grid {
    left: 85%;
  }

  .energy-seller {
    left: 50%;
  }

  .energyflow-container {
    max-width: 100%;
    aspect-ratio: 2 / 1;
  }

  .tabs-toggle-container {
    flex-direction: column;
    align-items: stretch;
  }

  .tab-buttons {
    justify-content: center;
  }

  .toggle-switch {
    justify-content: center;
  }
}
</style>